import jwtDecode from 'jwt-decode';
import { CacheService } from '../../services/cache-service';
import tenantService from '../../services/tenant-service';

type Tokens = {
  accessToken: string;
  refreshToken: string;
};

type AccessToken = {
  exp: number;
  userRoles: string[];
};

const expirationKey = 'pdc.user.expiration';
const accessTokenKey = 'pdc.user.accessToken';
const refreshTokenKey = 'pdc.user.refreshToken';
const rolesTokenKey = 'pdc.user.roles';

class AuthService {
  async logIn(formData: { system: string; username: string; password: string }): Promise<void> {
    const authUrl = await tenantService.getTenantAuthUrl(formData.system);
    const result = await fetch(`${authUrl}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: formData.username, password: formData.password }),
    });
    const tokens = await result.json() as Tokens;
    this.setTokens(tokens);
  }

  logOut(): void {
    this.deleteTokens();
  }

  isAuthed(): boolean {
    return Boolean(CacheService.get(accessTokenKey));
  }

  getAccessToken(): string {
    return CacheService.get(accessTokenKey);
  }

  async refreshToken(): Promise<void> {
    if (this.isValidToken()) {
      return;
    }
    const result = await fetch(`${CacheService.get('pdc.tenant.authentication')}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken: CacheService.get(accessTokenKey), refreshToken: CacheService.get(refreshTokenKey) }),
    });
    const tokens = await result.json() as Tokens;
    if (!tokens?.accessToken) {
      this.deleteTokens();
      window.location.reload();
      return;
    }
    this.setTokens(tokens);
  }

  isValidToken(): boolean {
    const milliseconds = 60000;
    const exp = CacheService.get(expirationKey);
    if (!exp) {
      return false;
    }
    return new Date(parseInt(exp, 10)).getTime() - milliseconds > new Date().getTime();
  }

  isSuperAdmin(): boolean {
    return CacheService.get('pdc.user.roles')?.toLowerCase().includes('super_admin');
  }

  private setTokens(tokens: Tokens): void {
    const decodedToken: AccessToken = jwtDecode(tokens.accessToken);
    CacheService.set(expirationKey, `${decodedToken.exp * 1000}`);
    CacheService.set(accessTokenKey, tokens.accessToken);
    CacheService.set(refreshTokenKey, tokens.refreshToken);
    CacheService.set(rolesTokenKey, decodedToken.userRoles.toString());
  }

  private deleteTokens(): void {
    CacheService.delete(expirationKey);
    CacheService.delete(accessTokenKey);
    CacheService.delete(refreshTokenKey);
    CacheService.delete(rolesTokenKey);
  }
}

export default new AuthService();
